import React, { useState } from 'react';
import { 
  Image as ImageIcon, 
  Video, 
  Music, 
  FileBox as FileGifBox, 
  Calendar, 
  HardDrive, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Share2, 
  Download, 
  ExternalLink 
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMedia } from '@/contexts/MediaContext';
import { useToast } from '@/components/ui/use-toast';

const typeMeta = {
  image: { icon: ImageIcon, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  video: { icon: Video, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  audio: { icon: Music, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  gif: { icon: FileGifBox, color: 'text-green-400', bg: 'bg-green-500/10' },
};

const formatSize = (size) => {
  if (!size && size !== 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let s = size;
  let i = 0;
  while (s >= 1024 && i < units.length - 1) {
    s /= 1024;
    i += 1;
  }
  return `${s.toFixed(1)} ${units[i]}`;
};

const MediaCard = ({
  item,
  thumbnailUrl,
  title,
  type,
  size,
  createdAt,
  onClick,
  onDelete,
  onRename,
  onShare,
  onDownload
}) => {
  const { deleteFile, updateFile } = useMedia();
  const { toast } = useToast();
  
  const card = item || {};
  const displayTitle = title || card.title || card.name || 'Mídia';
  const displayType = (type || card.type || card.category || 'mídia').toLowerCase();
  const displaySize = formatSize(size ?? card.size);
  const displayDate = createdAt || card.createdAt || card.date || card.modified;
  const Icon = typeMeta[displayType]?.icon || ImageIcon;
  const iconColor = typeMeta[displayType]?.color || 'text-slate-500';
  const iconBg = typeMeta[displayType]?.bg || 'bg-slate-800';
  const thumb = thumbnailUrl || card.thumbnail || card.thumbnailUrl;
  const isNew = displayDate && new Date(displayDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const handleDownloadAction = (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload();
      return;
    }
    const url = card.url || card.google_drive_url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast({
        title: 'Erro',
        description: 'Link de download indisponível.',
        variant: 'destructive',
      });
    }
  };

  const handleShareAction = async (e) => {
    e.stopPropagation();
    if (onShare) {
      onShare();
      return;
    }
    try {
      const shareUrl = `${window.location.origin}/share/${card.id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Link copiado!',
        description: 'Link copiado para a área de transferência.',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o link.',
        variant: 'destructive',
      });
    }
  };

  const handleRenameAction = async (e) => {
    e.stopPropagation();
    if (onRename) {
      onRename();
      return;
    }
    const newName = window.prompt('Novo nome para o arquivo:', displayTitle);
    if (!newName || newName.trim() === '' || newName === displayTitle) return;

    try {
      await updateFile(card.id, { name: newName.trim() });
      toast({
        title: 'Sucesso',
        description: 'Arquivo renomeado com sucesso.',
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Erro',
        description: 'Falha ao renomear arquivo.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteAction = async (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
      return;
    }
    if (window.confirm('Tem certeza que deseja excluir este arquivo?')) {
      try {
        await deleteFile(card.id);
        toast({
          title: 'Excluído',
          description: 'Arquivo movido para a lixeira.',
        });
      } catch (error) {
        toast({
          title: 'Erro',
          description: 'Falha ao excluir arquivo.',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm shadow-lg hover:shadow-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
    >
      <div className="aspect-video bg-slate-950/50 flex items-center justify-center overflow-hidden relative">
        {thumb ? (
          <img
            src={thumb}
            alt={displayTitle}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-90 group-hover:opacity-100"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80'; }}
          />
        ) : (
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${iconBg} shadow-inner`}>
            <Icon className={`h-8 w-8 ${iconColor}`} />
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {isNew && (
          <span className="absolute top-3 left-3 text-[10px] font-bold tracking-wider px-2 py-1 bg-cyan-500 text-white rounded-full shadow-lg shadow-cyan-500/20 z-10">
            NOVO
          </span>
        )}

        {/* Action Menu */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-800 text-slate-200">
              <DropdownMenuItem onClick={handleShareAction} className="focus:bg-slate-800 focus:text-cyan-400 cursor-pointer">
                <Share2 className="mr-2 h-4 w-4" />
                <span>Compartilhar</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadAction} className="focus:bg-slate-800 focus:text-cyan-400 cursor-pointer">
                <Download className="mr-2 h-4 w-4" />
                <span>Baixar</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleRenameAction} className="focus:bg-slate-800 focus:text-cyan-400 cursor-pointer">
                <Edit className="mr-2 h-4 w-4" />
                <span>Renomear</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem onClick={handleDeleteAction} className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer">
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Excluir</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-200 line-clamp-1 group-hover:text-cyan-400 transition-colors">{displayTitle}</h3>
            <p className="text-xs text-slate-400 capitalize mt-0.5">{displayType}</p>
          </div>
          <div className={`flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide border border-white/5 ${iconBg} ${iconColor}`}>
            <Icon className="h-3 w-3" />
            <span>{displayType}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs text-slate-500 font-medium pt-1 border-t border-slate-800/50">
          <span className="inline-flex items-center gap-1.5 hover:text-slate-300 transition-colors">
            <HardDrive className="h-3.5 w-3.5" />
            {displaySize}
          </span>
          {displayDate && (
            <span className="inline-flex items-center gap-1.5 hover:text-slate-300 transition-colors">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(displayDate).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaCard;

